import { ALL_SOCIALS } from './enums';
import {
  allSupportedDomains,
  optionalProtocol,
  optionalWWW,
  strictSpotifyPrefix,
  validSpotifyId,
} from './expressions';
import { IsValidDomain } from './types/functions';

/**
 * Validates that a url string is from a valid domain.
 */
const isValidDomain: IsValidDomain = (input) => {
  if (!isSupportedDomain(input)) {
    return false;
  }
  // Get type of url
  const type = input.match(`(${Object.values(ALL_SOCIALS).join('|')})`)[0];
  // Validate w/ specific validator
  switch (type) {
    // All of these domain follow the same standard URL pattern
    case ALL_SOCIALS.FACEBOOK:
      return isValidFacebook(input);
    case ALL_SOCIALS.INSTAGRAM:
      return isValidInstagram(input);
    case ALL_SOCIALS.TWITTER:
      return isValidTwitter(input);
    case ALL_SOCIALS.X:
      return isValidX(input);
    case ALL_SOCIALS.SOUNDCLOUD:
      return isValidSoundcloud(input);
    case ALL_SOCIALS.YOUTUBE:
      return isValidYoutube(input);
    case ALL_SOCIALS.SPOTIFY:
      return isValidSpotify(input);

    default:
      return false;
  }
};

export const isSupportedDomain: IsValidDomain = (input) => {
  const supportedDomainRegex = new RegExp(allSupportedDomains, 'gmi');

  if (supportedDomainRegex.exec(input) !== null) {
    return true;
  }

  return false;
};
/* eslint-disable no-useless-escape */
const isValidFacebook = (url: string) => {
  const validFacebookUrl = new RegExp(
    `^${optionalProtocol}${optionalWWW}(facebook.com\/)([A-Za-z0-9_.]{1,})$`,
    'i',
  );
  return validFacebookUrl.test(url);
};
const isValidInstagram = (url: string) => {
  const validInstagramUrl = new RegExp(
    `^${optionalProtocol}${optionalWWW}(instagram.com\/)([A-Za-z0-9_.]{1,})$`,
    'i',
  );
  return validInstagramUrl.test(url);
};
const isValidTwitter = (url: string) => {
  const validTwitterUrl = new RegExp(
    `^${optionalProtocol}${optionalWWW}(twitter.com\/)([A-Za-z0-9_]{1,})$`,
    'i',
  );
  return validTwitterUrl.test(url);
};
const isValidX = (url: string) => {
  const validXUrl = new RegExp(
    `^${optionalProtocol}${optionalWWW}(x.com\/)([A-Za-z0-9_]{1,})$`,
    'i',
  );
  return validXUrl.test(url);
};
const isValidSoundcloud = (url: string) => {
  const validSoundcloudUrl = new RegExp(
    `^${optionalProtocol}${optionalWWW}(soundcloud.com\/)([A-Za-z0-9_.-]{1,})$`,
    'i',
  );
  return validSoundcloudUrl.test(url);
};
/* eslint-enable no-useless-escape */
const isValidYoutube = (url: string) => {
  const normalized = url.replace(/\s/g, '');
  const withProto = /^https?:\/\//i.test(normalized)
    ? normalized
    : `https://${normalized}`;
  try {
    const u = new URL(withProto);
    let host = u.hostname.toLowerCase();
    if (host.startsWith('www.')) host = host.slice(4);
    if (host === 'm.youtube.com') host = 'youtube.com';
    if (host !== 'youtube.com') return false;
    const p = u.pathname.replace(/\/$/, '') || '/';
    if (/\/c\//i.test(p) || /\/user\//i.test(p)) return false;
    if (/^\/@[^/]+$/.test(p)) return true;
    if (/^\/channel\/(UC|HC)[A-Za-z0-9_-]{22}$/.test(p)) return true;
    return false;
  } catch {
    return false;
  }
};
const isValidSpotify = (url: string) => {
  const validSpotifyUrl = new RegExp(
    `^(${optionalProtocol}${strictSpotifyPrefix}(${validSpotifyId}))|(${optionalProtocol}((open.|play.)spotify.com/user/)(\\w+))$`,
    'i',
  );
  return validSpotifyUrl.test(url);
};

export default isValidDomain;
